import { createContext, useContext, useState, useEffect } from 'react';
import api, { formatApiErrorDetail } from '@/api/axios';
import { requestNotificationPermission, onForegroundMessage } from '@/firebase';
import { toast } from 'sonner';
import { SesionExpiradaDialog } from '@/components/SesionExpiradaDialog';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [sesionExpirada, setSesionExpirada] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Sesión vencida y sin renovación posible: pedir re-login SIN navegar
  // para no perder los formularios abiertos (ej. registro de llamada en Mi Día).
  useEffect(() => {
    const onExpired = () => {
      setUser((u) => {
        if (u && typeof u === 'object') setSesionExpirada(true);
        return u;
      });
    };
    window.addEventListener('sach:session-expired', onExpired);
    return () => window.removeEventListener('sach:session-expired', onExpired);
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Re-register push token on session restore
      try {
        if ('Notification' in window && 'serviceWorker' in navigator && Notification.permission === 'granted') {
          await requestNotificationPermission(api);
        }
      } catch (e) { /* silent */ }
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  // Listen for foreground push messages
  useEffect(() => {
    if (!user) return;
    // Clear badge when app is opened/focused
    if (navigator.clearAppBadge) {
      navigator.clearAppBadge().catch(() => {});
    }
    const handleFocus = () => {
      if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
    };
    window.addEventListener('focus', handleFocus);

    let unsubscribe = null;
    try {
      unsubscribe = onForegroundMessage((payload) => {
        const title = payload.notification?.title || 'Nuevo mensaje';
        const body = payload.notification?.body || '';
        toast.info(`${title}: ${body}`, { duration: 5000 });
        if (navigator.setAppBadge) {
          navigator.setAppBadge().catch(() => {});
        }
      });
    } catch (e) { /* silent */ }

    return () => {
      window.removeEventListener('focus', handleFocus);
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  const login = async (name, password) => {
    try {
      const payload = { name, password };
      const { data } = await api.post('/auth/login', payload);
      setUser(data);
      // Request push notification permission after login
      try {
        if ('Notification' in window && 'serviceWorker' in navigator) {
          await requestNotificationPermission(api);
        }
      } catch (e) {
        // Silent - push not available on this browser
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const register = async (email, password, name, username) => {
    try {
      const payload = { password, name };
      if (email) payload.email = email;
      if (username) payload.username = username;
      const { data } = await api.post('/auth/register', payload);
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      setUser(false);
      window.location.href = '/login';
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser: checkAuth,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sesionExpirada && user && typeof user === 'object' && (
        <SesionExpiradaDialog
          userName={user.name}
          onRelogin={async (password) => {
            const r = await login(user.name, password);
            if (r.success) {
              setSesionExpirada(false);
              toast.success('Sesión renovada ✅ Ya puedes volver a guardar.');
            }
            return r;
          }}
          onIrALogin={() => { setSesionExpirada(false); logout(); }}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
